import { toSlug } from "@/lib/toSlug";

import type {
  CustomFieldDefinition,
  CustomFieldType,
  LabeledValue,
} from "../types";

export interface CustomFieldDraft {
  key?: string;
  label?: string;
  type?: CustomFieldType;
  options?: Partial<LabeledValue>[];
}

const hasChoices = (type: CustomFieldType) =>
  type === "select" || type === "multi_select";

/** Keys must start with a letter so react-hook-form never mistakes them for array indexes. */
const toFieldKey = (label: string) => {
  const slug = toSlug(label) || "champ";
  return /^[a-z]/.test(slug) ? slug : `champ-${slug}`;
};

const ensureOptionValues = (options: Partial<LabeledValue>[] = []) =>
  options
    .filter((option) => option.label?.trim())
    .map((option) => ({
      label: option.label!.trim(),
      value: option.value || toSlug(option.label!) || "choix",
    }));

/**
 * Cleans custom field definitions before saving the configuration:
 * - drops rows without a label,
 * - gives new fields a unique key derived from their label (existing keys never change,
 *   so renaming a field keeps the values already entered),
 * - keeps choices only for list types and gives new choices a value.
 */
export const ensureCustomFieldKeys = (
  drafts: CustomFieldDraft[] | undefined,
): CustomFieldDefinition[] => {
  const definitions = (drafts ?? []).filter((draft) => draft.label?.trim());
  const usedKeys = new Set(
    definitions.map((draft) => draft.key).filter((key): key is string => !!key),
  );

  return definitions.map((draft) => {
    let key = draft.key;
    if (!key) {
      const base = toFieldKey(draft.label!);
      key = base;
      for (let suffix = 2; usedKeys.has(key); suffix++) {
        key = `${base}-${suffix}`;
      }
      usedKeys.add(key);
    }
    const type = draft.type ?? "text";
    return {
      key,
      label: draft.label!.trim(),
      type,
      ...(hasChoices(type)
        ? { options: ensureOptionValues(draft.options) }
        : {}),
    };
  });
};
